<template>
  <div class="dashboard-root" :class="{ 'dashboard-dark': isDark }">
    <!-- Control bar: shown only in edit mode. Edit mode is entered from the
         bottom-nav "More" menu ("Edit dashboard"), so there is no top Edit
         button. In edit mode the layout tabs + Reset + Done show here. -->
    <div v-if="!fieldsLoading && editing" class="dash-picker dash-picker--edit">
      <div class="dash-seg">
        <v-btn v-for="opt in layoutOptions" :key="opt.value" small depressed
          :class="['dash-tab', { 'dash-tab--active': layout === opt.value }]"
          @click="setLayout(opt.value)">{{ $t(opt.label) }}</v-btn>
      </div>
      <v-btn small text class="dash-reset" @click="showModulesDialog = true">
        <v-icon left small>mdi-view-grid-plus-outline</v-icon>{{ $t('message.dashboard.modules') }}
      </v-btn>
      <v-btn small text class="dash-reset" @click="resetLayout">
        <v-icon left small>mdi-restore</v-icon>{{ $t('message.dashboard.reset') }}
      </v-btn>
      <v-btn small depressed class="dash-edit dash-edit--on" @click="toggleEdit">
        <v-icon left small>mdi-check</v-icon>{{ $t('message.dashboard.done') }}
      </v-btn>
    </div>

    <!-- Loading skeleton -->
    <v-row no-gutters v-if="fieldsLoading">
      <v-col v-for="n in 12" :key="n" cols="4">
       <v-sheet class="mt-2 px-3">
        <v-skeleton-loader class="mx-auto" type="image" height="75"></v-skeleton-loader>
      </v-sheet>
      </v-col>
    </v-row>

    <div v-else class="px-0 mt-2 dashboard-body" :class="{ 'is-editing': editing }">
      <p v-if="editing" class="dash-tip text-center ma-0 pb-1">{{ layout === 'featured' ? $t('message.dashboard.editHintFeatured') : layout === 'bento' ? $t('message.dashboard.editHintBento') : $t('message.dashboard.editHint') }}</p>

      <!-- FEATURED layout: hero zone (pinned) on top + "all modules" grid below.
           Both share the drag group `feat`, so dragging a tile up pins it and
           dragging a hero down unpins it (prototype parity). -->
      <div v-if="layout === 'featured'" class="dashboard-body-inner px-2">
        <p class="dash-secthead px-1 mb-1">{{ $t('message.dashboard.featured') }}</p>
        <draggable v-model="featuredOrder" :disabled="!editing" class="dash-featured" handle=".dash-cell"
          :group="{ name: 'feat' }" :empty-insert-threshold="40" @end="persist">
          <div v-for="navItem in featuredTiles" :key="`hero_${idOf(navItem)}`" class="dash-feat-card dash-cell">
            <v-card flat class="dash-hero d-flex align-center pa-3" :to="editing ? null : navItem.to" @click="onTileClick(navItem)">
              <span class="dash-hero-ic mr-3 d-flex align-center justify-center">
                <svg v-if="navItem.icon === 'mdi-qrcode-scan'" style="width:26px;height:26px" viewBox="0 0 640 512"><path fill="currentColor" d="M368 64H96c-53 0-96 43-96 96 0 52.4 42.1 94.9 94.3 95.8L6.4 408C-6.9 431 1 460.3 24 473.6l55.4 32c23 13.3 52.3 5.4 65.6-17.6l133.9-232H368c8.8 0 16-7.2 16-16V80c0-8.8-7.2-16-16-16z"></path></svg>
                <span v-else-if="hasSvgIcon(navItem)" class="dash-svg-icon d-flex" v-html="navItem.icon_svg"></span>
                <v-icon v-else dark>{{ navItem.icon || 'mdi-cube' }}</v-icon>
              </span>
              <span class="dash-hero-title flex-grow-1 text-truncate">{{ tileLabel(navItem) }}</span>
              <v-icon v-if="!editing" class="dash-hero-arr">mdi-arrow-right</v-icon>
              <v-btn v-else icon small @click.stop.prevent="unpin(navItem)"><v-icon small>mdi-close</v-icon></v-btn>
            </v-card>
          </div>
          <div v-if="editing && !featuredTiles.length" class="dash-feat-empty text-center py-4">{{ $t('message.dashboard.dragToFeature') }}</div>
        </draggable>
        <p class="dash-secthead px-1 mb-1 mt-3">{{ $t('message.dashboard.allModules') }}</p>
        <draggable v-model="tileOrder" :disabled="!editing" class="dash-fgrid" handle=".dash-cell"
          :group="{ name: 'feat' }" @end="persist">
          <div v-for="navItem in orderedTiles" :key="`fcell_${idOf(navItem)}`" class="dash-fcell dash-cell">
            <v-card flat class="dash-fcell-inner d-flex flex-column align-center justify-center pa-2 fill-height"
              :to="editing ? null : navItem.to" @click="onTileClick(navItem)">
              <span class="dash-fcell-ic d-flex align-center justify-center mb-1">
                <svg v-if="navItem.icon === 'mdi-qrcode-scan'" style="width:24px;height:24px" viewBox="0 0 640 512"><path fill="currentColor" d="M368 64H96c-53 0-96 43-96 96 0 52.4 42.1 94.9 94.3 95.8L6.4 408C-6.9 431 1 460.3 24 473.6l55.4 32c23 13.3 52.3 5.4 65.6-17.6l133.9-232H368c8.8 0 16-7.2 16-16V80c0-8.8-7.2-16-16-16z"></path></svg>
                <span v-else-if="hasSvgIcon(navItem)" class="dash-svg-icon d-flex" v-html="navItem.icon_svg"></span>
                <v-icon v-else>{{ navItem.icon || 'mdi-cube' }}</v-icon>
              </span>
              <span class="dash-fcell-label text-center">{{ tileLabel(navItem) }}</span>
            </v-card>
          </div>
        </draggable>
      </div>

      <!-- BENTO layout -->
      <div v-else-if="layout === 'bento'" class="dashboard-body-inner">
        <draggable v-model="tileOrder" :disabled="!editing" class="dash-bento px-2" handle=".dash-cell" @end="persist">
          <div v-for="navItem in orderedTiles" :key="`bento_${idOf(navItem)}`"
            class="dash-bento-cell dash-cell" :class="{ 'dash-bento-wide': isWide(navItem) }">
            <v-card tile flat class="dash-bento-inner fill-height d-flex align-center justify-center"
              :to="editing ? null : navItem.to" @click="onTileClick(navItem)">
              <div class="d-flex align-center" :class="isWide(navItem) ? 'flex-row' : 'flex-column text-center'">
                <v-badge v-if="navItem.icon === 'mdi-newspaper-variant-outline' && unread_count > 0" :content="unread_count" overlap>
                  <span class="dash-bento-ic d-flex align-center justify-center"><v-icon>{{ navItem.icon || 'mdi-cube' }}</v-icon></span>
                </v-badge>
                <span v-else class="dash-bento-ic d-flex align-center justify-center">
                  <svg v-if="navItem.icon === 'mdi-qrcode-scan'" style="width:24px;height:24px" viewBox="0 0 640 512"><path fill="currentColor" d="M368 64H96c-53 0-96 43-96 96 0 52.4 42.1 94.9 94.3 95.8L6.4 408C-6.9 431 1 460.3 24 473.6l55.4 32c23 13.3 52.3 5.4 65.6-17.6l133.9-232H368c8.8 0 16-7.2 16-16V80c0-8.8-7.2-16-16-16z"></path></svg>
                  <span v-else-if="hasSvgIcon(navItem)" class="dash-svg-icon d-flex" v-html="navItem.icon_svg"></span>
                  <v-icon v-else>{{ navItem.icon || 'mdi-cube' }}</v-icon>
                </span>
                <div class="font-weight-medium" :class="isWide(navItem) ? 'ml-3 dash-bento-wide-label' : 'mt-1'">{{ tileLabel(navItem) }}</div>
              </div>
            </v-card>
            <div v-if="editing" class="dash-bento-exp">
              <v-btn icon x-small @click.stop.prevent="toggleWide(navItem)"><v-icon x-small>{{ isWide(navItem) ? 'mdi-arrow-collapse-horizontal' : 'mdi-arrow-expand-horizontal' }}</v-icon></v-btn>
            </div>
          </div>
        </draggable>
      </div>

      <!-- TILES + SECTIONS layouts: boxed cards (3-col) with blue icon chip.
           Sections just adds fixed group headers above each grid. -->
      <div v-else class="dashboard-body-inner px-2">
        <template v-for="(group, gIndex) in displayGroups">
          <p v-if="layout === 'sections'" :key="`head_${gIndex}`" class="dash-secthead px-1 mb-1" :class="{ 'mt-3': gIndex > 0 }">{{ $t(group.label) }}</p>
          <draggable :key="`grp_${gIndex}`" :value="group.list" :disabled="!editing" class="dash-tgrid" handle=".dash-cell"
            @input="onGroupReorder(gIndex, $event)" @end="persist">
            <div v-for="navItem in group.list" :key="`${idOf(navItem)}_module`" class="dash-tcell dash-cell">
              <v-card flat class="dash-tcell-inner d-flex flex-column align-center justify-center pa-3 fill-height"
                :to="editing ? null : navItem.to" @click="onTileClick(navItem)">
                <v-badge v-if="navItem.icon === 'mdi-newspaper-variant-outline' && unread_count > 0" :content="unread_count" overlap>
                  <span class="dash-tcell-ic d-flex align-center justify-center"><v-icon>{{ navItem.icon || 'mdi-cube' }}</v-icon></span>
                </v-badge>
                <span v-else class="dash-tcell-ic d-flex align-center justify-center">
                  <svg v-if="navItem.icon === 'mdi-qrcode-scan'" style="width:24px;height:24px" viewBox="0 0 640 512"><path fill="currentColor" d="M368 64H96c-53 0-96 43-96 96 0 52.4 42.1 94.9 94.3 95.8L6.4 408C-6.9 431 1 460.3 24 473.6l55.4 32c23 13.3 52.3 5.4 65.6-17.6l133.9-232H368c8.8 0 16-7.2 16-16V80c0-8.8-7.2-16-16-16z"></path></svg>
                  <span v-else-if="hasSvgIcon(navItem)" class="dash-svg-icon d-flex" v-html="navItem.icon_svg"></span>
                  <v-icon v-else>{{ navItem.icon || 'mdi-cube' }}</v-icon>
                </span>
                <span class="dash-tcell-label text-center mt-2">{{ tileLabel(navItem) }}</span>
              </v-card>
            </div>
          </draggable>
        </template>
      </div>
    </div>
    <!-- SCAN BAR CODE -->
    <v-dialog v-model="scanDialog" persistent max-width="500px" class="mt-0">
      <v-card>
        <v-card-title>
          {{$t('message.common.scan')}}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-1">
          {{ scanError }}
            <div>
              <video id="video" style="border: 1px solid gray; width:100%; height:auto;" ref="video"></video>
            </div>
            <div id="sourceSelectPanel" v-if="videoInputDevices.length > 1">
              <v-select id="sourceSelect" v-model="selectedDeviceId" outlined :items="videoInputDevices" hide-details item-text="label" item-value="deviceId" :label="$t('message.common.changeVideoSource')"></v-select>
            </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" id="scanClose" @click="stopScanningSession">{{ $t('message.common.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showProjectSelectDialog" persistent max-width="500px" class="mt-0">
      <v-card>
        <v-card-title>
          {{ $t('message.projects.selectProject') }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-1">
          <v-sheet v-if="isProjectsByUsedProductLoading" class="d-flex justify-center pa-5">
            <v-progress-circular indeterminate></v-progress-circular>
          </v-sheet>
          <v-autocomplete
            v-else
            v-model="seletedProject"
            :items="selectableProjectForPickup"
            :item-text="item => `${item.po_ranked ? item.po_ranked : ''} ${item.project_name ? ' - ' + item.project_name : ''} ${item.ao_ranked ? ' - ' +  item.ao_ranked : ''} ${item.work_order_name ? ' - ' + item.work_order_name : ''}`"
            item-value="id"
            :label="$t('message.projects.selectProject')"
            @input="gotoPickup(seletedProject)"
            menu-props="auto, overflowY"
            outlined
            dense
          >
          </v-autocomplete>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="handleProjectSelection">{{ $t('message.common.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- CUSTOMIZE MODULES: add / remove the tiles shown on the dashboard.
         The list is every module the user's role allows; the switch toggles
         each tile's visibility (off = added to `hidden`). -->
    <v-dialog v-model="showModulesDialog" max-width="420px" scrollable>
      <v-card>
        <v-card-title>{{ $t('message.dashboard.customizeModules') }}</v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-0" style="max-height:60vh">
          <v-list dense>
            <v-list-item v-for="tile in flatTiles" :key="`mod_${idOf(tile)}`">
              <v-list-item-icon class="mr-3">
                <span v-if="hasSvgIcon(tile)" class="dash-svg-icon dash-svg-icon--list d-flex" v-html="tile.icon_svg"></span>
                <v-icon v-else>{{ tile.icon || 'mdi-cube' }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ tileLabel(tile) }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-switch :input-value="!isHidden(tile)" color="primary" hide-details dense
                  @change="toggleModule(tile)"></v-switch>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="showModulesDialog = false">{{ $t('message.common.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import { BrowserMultiFormatReader } from '@zxing/library'
import PermissionsMixin from '@/mixins/permissions'
import FeaturesMixin from '@/mixins/features_list'
import { hostAppApi } from '@/plugins/axios_settings'
import { mapGetters } from 'vuex'
import { loadLayout, saveLayout, clearLayout, fetchLayout, applyOrder, tileId, LAYOUTS } from '@/utils/dashboardLayout'
import { setDark } from '@/utils/theme'
import '@/assets/css/dashboard-layout.css'

export default {
  mixins: [PermissionsMixin, FeaturesMixin],
  components: {
    draggable: () => import('vuedraggable')
  },
  data () {
    const saved = loadLayout()
    // Role-based module access is hydrated synchronously from the last-known
    // cache so the grid filters correctly on first paint; updateFeaturesData()
    // refreshes it from check_and_get_tenant_features.
    let allowedSlugs = []
    let isPwaAdmin = false
    try {
      allowedSlugs = JSON.parse(window.localStorage.getItem('WelditPwa_pwaAllowedTiles') || '[]') || []
      isPwaAdmin = window.localStorage.getItem('WelditPwa_pwaIsAdmin') === '1'
    } catch (e) { /* ignore corrupt cache */ }
    return {
      // ---- dashboard layout customisation state (persisted to localStorage) ----
      editing: false,
      layout: LAYOUTS.includes(saved.layout) ? saved.layout : 'tiles',
      isDark: saved.theme === 'dark',
      order: saved.order, // preferred tile id order
      featured: saved.featured, // pinned tile ids
      wide: saved.wide, // bento wide tile ids
      hidden: saved.hidden, // tile ids the user removed from their dashboard
      bentoDefaultsApplied: !!saved.bentoDefaultsApplied, // one-time seed of wide tiles
      // role-based module access (from the host app); admins see every tile,
      // other roles only the slugs their role enables.
      allowedSlugs,
      isPwaAdmin,
      showModulesDialog: false, // "Customize modules" add/remove sheet
      layoutOptions: [
        { value: 'tiles', label: 'message.dashboard.layoutTiles' },
        { value: 'featured', label: 'message.dashboard.featured' },
        { value: 'sections', label: 'message.dashboard.layoutSections' },
        { value: 'bento', label: 'message.dashboard.layoutBento' }
      ],
      // Sections layout: three fixed groups, matching the prototype. Each tile is
      // routed to a group by its stable id (route); anything unmatched goes to "Info & More".
      sectionWorkIds: ['/stemp', '/deviations', '/projects', '/calendar', '/global_checklist'],
      sectionResourceIds: ['/files', '/equipments', '/chemicallist', '/warehouse'],
      // tiles that default to wide in the Bento layout (prototype defaults).
      bentoDefaultWideIds: ['/stemp'],
      allTiles: {
        stemp: { icon: 'mdi-timer', to: '/stemp', name: 'message.layout.stemp' },
        deviations: { icon: 'mdi-comment-alert-outline', to: '/deviations', name: 'message.layout.deviation' },
        projects: { icon: 'mdi-server', to: '/projects', name: 'message.layout.projects' },
        calendar: { icon: 'mdi-calendar', to: '/calendar', name: 'message.layout.calendar' },
        files: { icon: 'mdi-folder-multiple', to: '/files', name: 'message.layout.files' },
        checklists: { icon: 'mdi-book-plus', to: '/global_checklist', name: 'message.layout.checklists' },
        equipments: { icon: 'mdi-developer-board', to: '/equipments', name: 'message.layout.equipments' },
        chemicallist: { icon: 'mdi-flask', to: '/chemicallist', name: 'message.layout.chemicallist' },
        warehouse: { icon: 'mdi-database', to: '/warehouse', name: 'message.layout.warehouse' }
      },
      stempItem: { _id: 'static_item1', icon: 'mdi-timer', to: '/stemp', isStatic: true, name: 'message.layout.stemp' },
      deviationItem: { _id: 'static_item2', icon: 'mdi-leak', to: '/deviations', isStatic: true, name: 'message.layout.deviation' },
      projectItem: { _id: 'static_item3', icon: 'mdi-note-multiple-outline', to: '/projects', isStatic: true, name: 'message.layout.projects' },
      calendarItem: { _id: 'static_item4', icon: 'mdi-calendar', to: '/calendar', isStatic: true, name: 'message.layout.calendar' },
      fileItem: { _id: 'static_item5', icon: 'mdi-file', to: '/files', isStatic: true, name: 'message.layout.files' },
      checklistItem: { _id: 'static_item6', icon: 'mdi-book-plus', to: '/global_checklist', isStatic: true, name: 'message.layout.checklists' },
      equipmentItem: { _id: 'static_item7', icon: 'mdi-developer-board', to: '/equipments', isStatic: true, name: 'message.layout.equipments' },
      warehouseItem: { _id: 'static_item8', icon: 'mdi-warehouse', to: '/warehouse', isStatic: true, name: 'message.layout.warehouse' },
      fieldsLoading: false,
      reloadApp: false,
      searchTerm: '',
      scanDialog: false,
      scanError: '',
      codeReader: {},
      videoInputDevices: [],
      selectedDeviceId: window.localStorage.getItem('WelditPwa_storeCamera') ? window.localStorage.getItem('WelditPwa_storeCamera') : 0,
      showProjectSelectDialog: false,
      selectableProjectForPickup: [],
      isProjectsByUsedProductLoading: false,
      selectedProductCode: null,
      seletedProject: null,
      unread_count: 0
    }
  },
  computed: {
    ...mapGetters(['isAuthenticated', 'getNewContentAvailable', 'timerData', 'getuserDetails', 'getHostRefApi', 'userId']),
    commonList () {
      const navItems = []
      if (!this.$store.state.common.isStempExcluded) navItems.push(this.stempItem)
      // if (!this.$store.state.common.isStempExcluded) navItems.push(this.stempsItem)
      if (!this.$store.state.common.isDeviationExcluded) navItems.push(this.deviationItem)
      if (!this.$store.state.common.isProjectExcluded) navItems.push(this.projectItem)
      navItems.push(this.calendarItem)
      navItems.push(this.fileItem)
      if (this.$store.state.common.includeGlobalChecklist) navItems.push(this.checklistItem)
      if (!this.$store.state.common.isEquipmentExcluded) {
        if (this.checkActionPermission(this.CONSTANTS.equipment, this.CONSTANTS.LIST)) navItems.push(this.equipmentItem)
      }
      if (!this.$store.state.common.isWarehouseExcluded) {
        if (this.checkFeatureEnabled('warehouse') && this.checkActionPermission(this.CONSTANTS.warehouse, this.CONSTANTS.LIST)) navItems.push(this.warehouseItem)
      }
      return [
        { id: 'navigationItems', list: navItems }
      ]
    },
    tilesList () {
      let navItems = []
      Object.keys(this.allTiles).forEach(key => {
        var checklistFiles = ''
        if (key === 'checklist_files') {
          key = 'checklists'
          checklistFiles = 'checklist_files'
        } else checklistFiles = ''
        const featureObj = this.getFeatureObj(key)
        // Inclusion rules mirror the (legacy) commonList so core modules aren't
        // dropped just because the tenant's feature payload omits them:
        //  - Calendar & Files: always shown (core, no feature flag)
        //  - Stemp: the feature is keyed `timer`, gated by the isStempExcluded flag
        //  - Deviation / Projects: shown unless excluded by their store flag
        //  - Checklists: shown when global checklist is included
        //  - Equipments: permission check; Warehouse: feature + permission
        //  - everything else: original feature-flag + permission gate
        const state = this.$store.state.common
        let include
        switch (key) {
          case 'calendar':
          case 'files':
            include = true
            break
          case 'stemp':
            include = !state.isStempExcluded && Number(this.checkFeatureEnabled('timer'))
            break
          case 'deviations':
            include = !state.isDeviationExcluded
            break
          case 'projects':
            include = !state.isProjectExcluded
            break
          case 'checklists':
            include = !!state.includeGlobalChecklist
            break
          case 'equipments':
            include = this.checkActionPermission(this.CONSTANTS.equipment, this.CONSTANTS.LIST)
            break
          case 'warehouse':
            include = !state.isWarehouseExcluded && !!this.checkFeatureEnabled('warehouse') &&
              this.checkActionPermission(this.CONSTANTS.warehouse, this.CONSTANTS.LIST)
            break
          default:
            // Visibility is driven by the tenant feature flag only; the role's
            // PWA config (allowedSlugs, applied below) decides which of the
            // feature-enabled modules actually appear. Module-level permission
            // is NOT a visibility gate here — e.g. chemicallist shows whenever
            // its feature is on and the role enables the tile.
            include = !!(featureObj && featureObj.enabled)
        }
        // Role-based module access: non-admin roles only see the tiles their
        // role enables. `key` is still the tile slug here (it matches the host
        // app's app_tiles.slug). Fail open when the allowed set is unknown
        // (older backend / not yet fetched) so the dashboard is never empty.
        if (include && !this.isPwaAdmin && this.allowedSlugs.length) {
          include = this.allowedSlugs.includes(key)
        }
        if (include) {
          let translatedLabel = (featureObj && featureObj.label) ? featureObj.label : this.$t(this.allTiles[key].name)
          switch (featureObj && featureObj.label) {
            case 'Stemp':
              translatedLabel = this.$t('message.layout.stemp')
              break
            case 'Deviations':
              translatedLabel = this.$t('message.layout.deviation')
              break
            case 'Projects':
              translatedLabel = this.$t('message.layout.projects')
              break
            case 'Calendar':
              translatedLabel = this.$t('message.layout.calendar')
              break
            case 'Checklists':
              translatedLabel = this.$t('message.layout.checklists')
              break
            case 'Files':
              translatedLabel = this.$t('message.layout.files')
              break
            case 'Chemical List':
              translatedLabel = this.$t('message.layout.chemicallist')
              break
            case 'Kjemisk liste':
              translatedLabel = this.$t('message.layout.chemicallist')
              break
            case 'Cheminių medžiagų sąrašas':
              translatedLabel = this.$t('message.layout.chemicallist')
              break
            case 'Lista substancji chemicznych':
              translatedLabel = this.$t('message.layout.chemicallist')
              break
            case 'Equipments':
              translatedLabel = this.$t('message.layout.equipments')
              break
            case 'Warehouse':
              translatedLabel = this.$t('message.layout.warehouse')
              break
            default:
          }
          key = checklistFiles === 'checklist_files' ? 'checklist_files' : key
          this.allTiles[key].name = checklistFiles === 'checklist_files' ? 'Checklist files' : translatedLabel
          // Built-in tiles keep their predefined mdi icon, but if the tenant
          // chose a WeldIt SVG icon for this tile in the host app the feature
          // payload carries the resolved markup (icon_svg) — prefer it. Pushed
          // as a fresh shallow copy each render so a tile whose SVG was later
          // cleared falls back to the predefined icon (no stale icon_svg).
          navItems.push({ ...this.allTiles[key], icon_svg: (featureObj && featureObj.icon_svg) ? featureObj.icon_svg : null })
        }
      })
      // Role-based gating also applies to the quick-access and custom-module
      // tiles (they used to bypass it). `scan_qr_code` is a real app_tiles slug,
      // so it is role-controlled like any module. News has no role-catalog slug
      // and is a core quick-access, so it always stays available.
      const restrict = !this.isPwaAdmin && this.allowedSlugs.length
      if (!restrict || this.allowedSlugs.includes('scan_qr_code')) {
        navItems.push({ icon: 'mdi-qrcode-scan', name: this.$t('message.mixed.scanQRCode'), slug: 'scan_qr_code' })
      }
      navItems.push({ icon: 'mdi-newspaper-variant-outline', name: this.$t('message.news.news'), to: '/news', slug: 'news' })
      // Custom modules are role-gated by their slug, exactly like built-in tiles —
      // a custom module the role disabled must not appear in the grid or the
      // Customize-modules dialog.
      let customModules = this.getCustomModules() || []
      customModules = customModules.filter(m => this.allowedSlugs.includes(m.slug))
      if (customModules.length) navItems = [...navItems, ...customModules]
      return [
        { id: 'navigationItems', list: navItems }
      ]
    },
    /* ---------- derived views for the customisable dashboard ---------- */
    // Flat list of all available tiles (feature/permission filtered upstream).
    flatTiles () {
      return (this.tilesList[0] && this.tilesList[0].list) ? this.tilesList[0].list : []
    },
    // All tiles in the user's saved order. Featured pinning is a Featured-layout
    // concept only: the hero zone shows pinned tiles and the "all modules" grid
    // shows the rest. The Tiles / Sections / Bento layouts have no hero zone, so
    // there we must show EVERY tile — otherwise tiles pinned to `featured` would
    // silently vanish from those layouts.
    orderedTiles () {
      const visible = this.flatTiles.filter(t => !this.hidden.includes(this.idOf(t)))
      const ordered = applyOrder(visible, this.order)
      if (this.layout !== 'featured') return ordered
      return ordered.filter(t => !this.featured.includes(this.idOf(t)))
    },
    // Pinned tiles, in the saved featured order. Hidden tiles never surface.
    featuredTiles () {
      const byId = new Map(this.flatTiles
        .filter(t => !this.hidden.includes(this.idOf(t)))
        .map(t => [this.idOf(t), t]))
      return this.featured.map(id => byId.get(id)).filter(Boolean)
    },
    // Groups for the Sections layout: three fixed groups (Work / Resources /
    // Info & More), matching the prototype. Empty groups are dropped.
    displayGroups () {
      if (this.layout !== 'sections') {
        return [{ label: 'message.dashboard.allModules', list: this.orderedTiles }]
      }
      const work = []; const resources = []; const more = []
      this.orderedTiles.forEach(t => {
        const id = this.idOf(t)
        if (this.sectionWorkIds.includes(id)) work.push(t)
        else if (this.sectionResourceIds.includes(id)) resources.push(t)
        else more.push(t)
      })
      const groups = []
      if (work.length) groups.push({ label: 'message.dashboard.sectionWork', list: work })
      if (resources.length) groups.push({ label: 'message.dashboard.sectionResources', list: resources })
      if (more.length) groups.push({ label: 'message.dashboard.sectionInfoMore', list: more })
      return groups
    },
    // v-model proxy for dragging the tile grid / bento / featured "all modules".
    // A tile landing here is implicitly unpinned (removed from featured).
    tileOrder: {
      get () { return this.orderedTiles },
      set (list) {
        const visibleIds = list.map(t => this.idOf(t))
        // any previously-featured id now present in the grid is being unpinned
        this.featured = this.featured.filter(id => !visibleIds.includes(id))
        this.commitOrder(list)
      }
    },
    // v-model proxy for dragging the featured hero zone. Items here become pinned.
    featuredOrder: {
      get () { return this.featuredTiles },
      set (list) { this.featured = list.map(t => this.idOf(t)); this.persist() }
    }
  },
  async beforeRouteEnter (to, from, next) {
    await next(vm => {
      if (from && from.path === '/login') vm.reloadApp = true
    })
  },
  created () {
    this.fieldsLoading = false
    this.$store.dispatch('getGeneralSettings')
    // Always re-pull tenant features (and pwa_allowed_tiles / pwa_is_admin) when
    // the dashboard opens — including SPA navigation, when App.vue's boot fetch
    // does not re-run — so role changes show without a hard reload. updateFeaturesData
    // already guards on the auth token; we only need a host to call.
    if (this.getHostRefApi) {
      this.updateFeaturesData()
    }
    // hydrate the layout from the authoritative server config (data() seeded it
    // from the synchronous cache; this refreshes it across devices)
    this.hydrateLayout()
  },
  mounted () {
    this.getUnreadNewsCount()
    this.$nextTick(() => this.seedBentoDefaults())
    this.applyBodyTheme()
    // edit mode is entered from the global "More" menu (no top Edit button)
    this.$root.$on('enterDashboardEdit', this.startEditing)
  },
  beforeDestroy () {
    // leaving the dashboard: clear the dashboard-only full-page background classes.
    // The light/dark theme itself is global now (Vuetify) and must persist across
    // pages, so we only tell the header which logo to show for the current theme —
    // we do NOT reset the app to light here.
    document.body.classList.remove('dashboard-body-bg', 'dashboard-body-bg--dark')
    this.$root.$emit('dashboardTheme', this.isDark)
    this.$root.$off('enterDashboardEdit', this.startEditing)
  },
  methods: {
    /* ---------- dashboard customisation ---------- */
    idOf (tile) { return tileId(tile) },
    // Whether a tile carries a WeldIt SVG icon (uploaded/built-in) resolved by
    // the host app. `icon_svg` is server-sanitized markup (see the host's
    // sanitizeSvg()), so it is safe to inject with v-html. When absent we fall
    // back to the `icon` Font Awesome / mdi class as before.
    hasSvgIcon (tile) { return !!(tile && tile.icon_svg) },
    // Tile labels are a mix of i18n keys (`message.layout.files`, built-in
    // tiles) and already-resolved plain text (custom modules carry the
    // tenant's own label, e.g. "VD test"). Only run the plain ones through
    // $t() would emit a "Cannot translate the value of keypath" warning, so
    // translate keys and pass everything else through verbatim.
    tileLabel (tile) {
      const name = (tile && tile.name) || ''
      return /^message\./.test(name) ? this.$t(name) : name
    },
    // Pull the authoritative config from the API and apply it to the reactive
    // state. Falls back to the cached config when the server is unreachable.
    async hydrateLayout () {
      const saved = await fetchLayout()
      this.layout = LAYOUTS.includes(saved.layout) ? saved.layout : 'tiles'
      this.isDark = saved.theme === 'dark'
      this.order = saved.order
      this.featured = saved.featured
      this.wide = saved.wide
      this.hidden = saved.hidden
      this.bentoDefaultsApplied = !!saved.bentoDefaultsApplied
      // re-apply the (possibly updated) theme app-wide
      this.applyBodyTheme()
      setDark(this.$vuetify, this.isDark)
      this.$nextTick(() => this.seedBentoDefaults())
    },
    // Paint the whole page (html/body, incl. side margins) to match the dashboard
    // theme. Lives on <body> so it covers the entire viewport, not just this component.
    applyBodyTheme () {
      document.body.classList.add('dashboard-body-bg')
      document.body.classList.toggle('dashboard-body-bg--dark', this.isDark)
      // notify the shared header so it can swap to the dark-mode logo
      this.$root.$emit('dashboardTheme', this.isDark)
    },
    onTileClick (navItem) {
      if (this.editing) return // taps are inert in edit mode; drag handles reorder
      if (navItem.isCustomModules) this.openInNewTab(navItem)
      else if (navItem.name === this.$t('message.mixed.scanQRCode')) this.scanning()
      // built-in routed tiles navigate via :to
    },
    setLayout (value) {
      this.layout = value
      this.persist()
    },
    toggleEdit () { this.editing = !this.editing },
    startEditing () { this.editing = true },
    toggleTheme () {
      this.isDark = !this.isDark
      this.applyBodyTheme()
      // drive the global app theme (Vuetify) so every page follows, not just the dashboard
      setDark(this.$vuetify, this.isDark)
      this.persist()
    },
    isFeatured (tile) { return this.featured.includes(this.idOf(tile)) },
    togglePin (tile) {
      const id = this.idOf(tile)
      if (this.featured.includes(id)) this.featured = this.featured.filter(f => f !== id)
      else this.featured = [...this.featured, id]
      this.persist()
    },
    unpin (tile) {
      this.featured = this.featured.filter(f => f !== this.idOf(tile))
      this.persist()
    },
    // --- add / remove modules (per-user visibility within the role's set) ---
    isHidden (tile) { return this.hidden.includes(this.idOf(tile)) },
    toggleModule (tile) {
      const id = this.idOf(tile)
      if (this.hidden.includes(id)) {
        this.hidden = this.hidden.filter(h => h !== id)
      } else {
        this.hidden = [...this.hidden, id]
        // a hidden tile can't stay pinned to the Featured hero zone
        this.featured = this.featured.filter(f => f !== id)
      }
      this.persist()
    },
    isWide (tile) { return this.wide.includes(this.idOf(tile)) },
    toggleWide (tile) {
      const id = this.idOf(tile)
      if (this.wide.includes(id)) this.wide = this.wide.filter(w => w !== id)
      else this.wide = [...this.wide, id]
      this.persist()
    },
    // A drag inside the single-group grid / bento produced a new visible order;
    // splice it back into the full saved order so pinned/hidden ids are kept.
    commitOrder (visibleList) {
      const visibleIds = visibleList.map(t => this.idOf(t))
      // keep featured ids (not shown in the grid) ahead, then the new order
      this.order = [...this.featured.filter(id => !visibleIds.includes(id)), ...visibleIds]
      this.persist()
    },
    onGroupReorder (groupIndex, newList) {
      // Rebuild the full visible order from all groups, swapping in the changed one.
      const groups = this.displayGroups.map((g, i) => (i === groupIndex ? newList : g.list))
      this.commitOrder([].concat(...groups))
    },
    // Seed of the prototype's default-wide bento tiles. Applied to local state
    // only — we deliberately do NOT persist() here, so simply landing on the
    // dashboard never triggers a save (flushSave). The seed is idempotent, so it
    // re-applies on each load until the user's first real change persists
    // bentoDefaultsApplied (and their resizes) along with it.
    seedBentoDefaults () {
      if (this.bentoDefaultsApplied) return
      const wide = new Set(this.wide)
      this.flatTiles.forEach(t => {
        const id = this.idOf(t)
        const isQuickWide = t.icon === 'mdi-qrcode-scan' || t.icon === 'mdi-newspaper-variant-outline'
        if (this.bentoDefaultWideIds.includes(id) || isQuickWide) wide.add(id)
      })
      this.wide = [...wide]
      this.bentoDefaultsApplied = true
    },
    persist () {
      saveLayout({
        version: 1,
        theme: this.isDark ? 'dark' : 'light',
        layout: this.layout,
        order: this.order,
        featured: this.featured,
        wide: this.wide,
        hidden: this.hidden,
        bentoDefaultsApplied: this.bentoDefaultsApplied
      })
    },
    resetLayout () {
      clearLayout()
      this.order = []
      this.featured = []
      this.wide = []
      this.hidden = []
      this.bentoDefaultsApplied = false
      this.layout = 'tiles'
      this.editing = false
      this.$nextTick(() => this.seedBentoDefaults())
    },
    openInNewTab (cModule) {
      if (cModule.link) window.open(cModule.link, '_blank')
    },
    setBorderRight (itemPosition, list) {
      // const isNotLastItem = itemPosition < list.length
      return (itemPosition % 3) !== 0 ? 'grid-border-right' : '' // && isNotLastItem add after !== 0
    },
    setBorderBottom (itemPosition, list) {
      const noOf3s = Math.floor(list.length / 3)
      const remainingNos = list.length % 3
      if (remainingNos) {
        return itemPosition <= (noOf3s * 3) ? 'grid-border-bottom' : ''
      } else {
        let nextRow = [0, 1].includes(remainingNos) ? 3 : 1
        if (remainingNos === 2) nextRow = 2
        return (itemPosition + nextRow) <= (noOf3s * 3) ? 'grid-border-bottom' : ''
      }
    },
    scanning (module) {
      const self = this
      this.scanDialog = true
      self.scanError = ''
      self.codeReader = new BrowserMultiFormatReader()
      self.codeReader.getVideoInputDevices().then((videoInputDevices) => {
        self.videoInputDevices = videoInputDevices
        self.selectedDeviceId = this.$vuetify.breakpoint.mobile && videoInputDevices.length > 1 ? videoInputDevices[1].deviceId : videoInputDevices[0].deviceId
        this.scanFromVideoSource(self.selectedDeviceId, module)
      }).catch((err) => {
        console.log(err)
      })
    },
    scanFromVideoSource (videoDeviceId, module) {
      if (videoDeviceId) window.localStorage.setItem('WelditPwa_storeCamera', videoDeviceId)
      this.codeReader.decodeOnceFromVideoDevice(videoDeviceId, 'video')
        .then((result) => {
          this.searchTerm = result.text
          this.stopScanningSession()
          setTimeout(() => {
            this.getRespectiveValue()
          }, 50)
        }).catch((err) => {
          if (err && err.name && err.name === 'NotAllowedError') alert(this.$t('message.common.permissionDenied'))
        })
    },
    stopScanningSession () {
      this.scanDialog = false
      this.codeReader.reset()
      this.codeReader.stopContinuousDecode()
      this.codeReader = {}
    },

    /**
     * Parses QR code and query parameters from `searchTerm`, sends them to the QR code API,
     * and navigates to the corresponding page based on the decoded result.
     * Supports routing for projects, equipment, products, chemical lists, warehouse orders, and custom modules.
     * Displays a snackbar error if the QR code is invalid or no matching result is found.
     * @return {void} Navigates or triggers actions; shows an error if no valid result.
    */
    async getRespectiveValue () {
      const navigate = (path) => this.$router.push(path || '/not_found')
      const normalizeModuleName = (name) => ({ deviation: 'deviations', 'project-checklist': 'checklists' }[name] ?? name)
      const showError = (msgKey = 'message.mixed.barCodeNotDecoded') =>
        this.$root.$emit('snackbar', { snackbar: true, color: 'error', text: msgKey })

      const searchTermString = String(this.searchTerm || '')
      const [pathPart = '', queryString = ''] = searchTermString.split('?')
      const pathSegments = pathPart.split('/').filter(Boolean)
      const barCodeValue = pathSegments[0] || null

      if (!barCodeValue) return showError()

      const queryParams = new URLSearchParams(queryString)
      const qrData = Object.fromEntries(queryParams.entries())
      const moduleName = normalizeModuleName(qrData.mid ?? qrData.m ?? '')
      const objectId = qrData.oid ?? qrData.o ?? ''
      const getQrFromPath = () => (pathSegments[pathSegments.length - 1] || '').replace('&', '-')
      try {
        const formData = new FormData()
        for (const [key, value] of queryParams.entries()) formData.append(key, value)

        const { data } = await hostAppApi.post(`${this.getHostRefApi}qrcode/${barCodeValue}`, formData)

        if (data?.status !== 'success') return showError()
        if (qrData.is_pr && qrData.tid && moduleName) return navigate(`/projects/${qrData.tid}/${moduleName}`)
        if (moduleName === 'equipment' && objectId) return navigate(`/equipments/${objectId}`)
        if (moduleName === 'product') return navigate(`/product_view?qr=${getQrFromPath()}`)
        if (qrData.m === 'equipment' && qrData.o) return navigate(`/equipments/${qrData.o}`)
        if (qrData.m === 'chemicallist' && qrData.o) return navigate(`/chemicallist/${qrData.o}`)

        if (['product', 'warehouseorder'].includes(qrData.m || '')) {
          this.selectedProductCode = (pathPart || '').slice(1)
          this.showProjectSelectDialog = true
          this.getProjectsByUsedProduct()
          return
        }

        if (qrData.m && qrData.d && qrData.o && qrData.i && qrData.c) {
          const routes = {
            drawing: `/projects/${qrData.o}/drawing/action/${qrData.c}`,
            'advanced-weld-log': `/projects/${qrData.o}/advanced-weld-log/${qrData.c}`,
            weldlog: `/projects/${qrData.o}/weld-log/action/${qrData.c}`,
            checklist: `/projects/${qrData.o}/checklists/${qrData.c}`
          }

          if (routes[qrData.m]) {
            return navigate(routes[qrData.m])
          }
          return showError()
        }

        if (qrData.m && qrData.c && qrData.o) {
          return navigate(`/projects/${qrData.o}/custom_modules/${qrData.c}?name=${qrData.m}`)
        }

        if (qrData.m && qrData.o) {
          return navigate(`/projects/${qrData.o}/${normalizeModuleName(qrData.m)}`)
        }
        return showError()
      } catch {
        return showError()
      }
    },
    updateFeaturesData () {
      this.$i18n.locale = window.localStorage.getItem(process.env.VUE_APP_LOCALE_STORAGE) ? window.localStorage.getItem(process.env.VUE_APP_LOCALE_STORAGE) : 'no'
      const authToken = this.$cookie.get(process.env.VUE_APP_TOKEN)
      if (authToken) {
        // cache-buster (_) so role/tile changes are never served from a cached
        // GET response — the allowed-tiles set must reflect the latest role config.
        hostAppApi.get(`${this.getHostRefApi}check_and_get_tenant_features?user_id=${this.userId}&_=${Date.now()}`, { headers: { language: this.$i18n.locale } })
          .then((response) => {
            if (response && response.data) {
              if (response.data.features) {
                const features = response.data.features
                this.$root.$emit('stempTimerActive', features)
                if (features) {
                  this.features = features
                  localStorage.setItem('features', JSON.stringify(features))
                }
              }
              // role-based module access: which tiles this user may show, and
              // whether they're an admin (every tile). Cached for offline / next
              // boot (see data()).
              // Only trust this when we actually queried a real user: right after
              // login the user_id cookie can still be 0, and the backend then
              // returns the "all tiles" fallback. Applying that would wrongly flip
              // a restricted dashboard to show everything, so we skip it and keep
              // the last good set until a fetch with a valid user_id arrives.
              if (this.userId) {
                this.allowedSlugs = Array.isArray(response.data.pwa_allowed_tiles) ? response.data.pwa_allowed_tiles : []
                this.isPwaAdmin = !!response.data.pwa_is_admin
                try {
                  window.localStorage.setItem('WelditPwa_pwaAllowedTiles', JSON.stringify(this.allowedSlugs))
                  window.localStorage.setItem('WelditPwa_pwaIsAdmin', this.isPwaAdmin ? '1' : '0')
                } catch (e) { /* storage disabled */ }
              }
            }
          })
      }
    },
    getProjectsByUsedProduct () {
      this.isProjectsByUsedProductLoading = true
      hostAppApi.get(`${this.getHostRefApi}get_active_projects?is_pwa_warehouse=1`)
        .then((response) => {
          this.selectableProjectForPickup = response.data
        })
        .catch()
        .finally(() => { this.isProjectsByUsedProductLoading = false })
    },
    gotoPickup (id) {
      this.showProjectSelectDialog = false
      this.$router.push(`projects/${id}/materials/pick/${this.selectedProductCode}`)
    },
    handleProjectSelection () {
      this.showProjectSelectDialog = false
      this.$router.push(`/product_view?qr=${this.selectedProductCode.replace('&', '-')}`)
    },
    getUnreadNewsCount () {
      hostAppApi.get(`${this.getHostRefApi}news/unread-count`)
        .then((response) => {
          this.unread_count = response?.data ?? 0
        })
    }
  },
  watch: {
    // keep the dashboard's local dark state in sync when the theme is toggled
    // elsewhere (e.g. the global nav menu) while the dashboard is open.
    '$vuetify.theme.dark' (val) {
      if (this.isDark !== val) {
        this.isDark = val
        this.applyBodyTheme()
        this.persist()
      }
    },
    reloadApp (val) {
      if (val) this.$router.go()
    },
    selectedDeviceId (val) {
      if (val) {
        window.localStorage.setItem('WelditPwa_storeCamera', val)
        this.scanFromVideoSource(val, this.module)
      }
    },
    '$i18n.locale' (val) {
      if (val) this.updateFeaturesData()
    }
  }
}
</script>
<style>
/* Inline WeldIt SVG icons injected via v-html (custom-module tiles). The chip
   containers (.dash-*-ic) already size/colour any child svg; the wrapper just
   needs to size itself to the chip and hand currentColor down to the svg. */
.dash-svg-icon { width: 24px; height: 24px; align-items: center; justify-content: center; }
.dash-svg-icon svg { width: 100%; height: 100%; color: inherit; fill: currentColor; }
.dash-hero-ic .dash-svg-icon { width: 26px; height: 26px; }
/* Customize-modules list rows: match Vuetify's list icon size. */
.dash-svg-icon--list { width: 24px; height: 24px; color: rgba(0, 0, 0, 0.54); }
.dashboard-dark .dash-svg-icon--list { color: rgba(255, 255, 255, 0.7); }
.dashboard_bg_color {
  /* background: #578ebe; */
  background: linear-gradient(180deg, grey 85%, #ffffff 15%);
  border-radius: 0 !important;
}
.grid-border-bottom {
  border-bottom: 0.85px solid #19191912 !important;
}
.grid-border-right {
  border-right: 0.85px solid #19191912 !important;
}
.custom-margin {
  margin-top: 28%;
}
.custom_bg_color {
  background: grey !important;
}
.custom_border_color_style {
  /* border: 15px solid #578ebe; */
  border-bottom: none;
  border-bottom-left-radius: 0px !important;
  border-bottom-right-radius: 0px !important;
  min-height: 30vh !important;
}
</style>
